import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import Header from "../components/Header";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { MessageThread } from "../components/MessageThread";
import { brl } from "../components/PurchaseDialog";
import { api, formatApiError } from "../lib/api";
import { ShoppingBag, MessageCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import PixPayment from "../components/PixPayment";

const STATUS_CLS = {
  awaiting_payment: "text-orange-500 border-orange-500/40",
  pending: "text-amber-500 border-amber-500/40", awaiting_info: "text-sky-500 border-sky-500/40",
  processing: "text-violet-500 border-violet-500/40", completed: "text-emerald-500 border-emerald-500/40",
  cancelled: "text-rose-500 border-rose-500/40",
};

export const OrderStatusBadge = ({ o }) => (
  <Badge variant="outline" className={STATUS_CLS[o.status]} data-testid={`order-status-${o.id}`}>{o.status_label}</Badge>
);

const PayPix = ({ o, onPaid }) => {
  const [pix, setPix] = useState(null);
  const open = async () => {
    try { const { data } = await api.post(`/store/orders/${o.id}/pix`); setPix(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  if (pix) return <PixPayment initial={pix} onPaid={() => { setPix(null); onPaid(); }} />;
  return (
    <Button size="sm" className="gold-btn mb-4" onClick={open} data-testid={`order-pay-pix-${o.id}`}>Pagar com Pix</Button>
  );
};

const ConfirmReceipt = ({ o, onDone }) => {
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    if (!window.confirm("Confirmar que você recebeu o produto/serviço? Depois disso o pedido é concluído e não pode ser cancelado.")) return;
    setBusy(true);
    try { await api.post(`/store/orders/${o.id}/confirm`); toast.success("Compra validada. Obrigado!"); onDone(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="mb-4 p-3 rounded-lg border border-emerald-500/40 bg-emerald-500/5 flex flex-wrap items-center justify-between gap-2 text-xs">
      <span>Já recebeu? Valide a compra para concluir o pedido.</span>
      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={busy} onClick={confirm} data-testid={`order-confirm-${o.id}`}>Confirmar recebimento</Button>
    </div>
  );
};

const OrderCard = ({ o, open, onToggle, onSend, onReload }) => (
  <Card className="p-5" data-testid={`order-card-${o.id}`}>
    <button type="button" className="w-full flex flex-wrap items-center justify-between gap-3 text-left" onClick={onToggle} data-testid={`order-toggle-${o.id}`}>
      <div>
        <div className="font-medium">#{o.code} · {o.item_name}</div>
        <div className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleString("pt-BR")} · {o.payment_method_label}</div>
      </div>
      <div className="flex items-center gap-2">
        {o.unread_user > 0 && (
          <Badge className="bg-rose-500 text-white" data-testid={`order-unread-${o.id}`}>
            <MessageCircle className="h-3 w-3 mr-1" /> {o.unread_user} nova{o.unread_user > 1 ? "s" : ""}
          </Badge>
        )}
        <span className="font-mono">{brl(o.price)}</span>
        <OrderStatusBadge o={o} />
      </div>
    </button>
    {open && (
      <div className="mt-4 pt-4 border-t border-border">
        {o.status === "awaiting_payment" && <PayPix o={o} onPaid={onReload} />}
        {o.can_confirm && <ConfirmReceipt o={o} onDone={onReload} />}
        <MessageThread messages={o.messages} viewer="user" onSend={onSend} testid={`order-thread-${o.id}`} />
      </div>
    )}
  </Card>
);

export default function MyOrders() {
  const [orders, setOrders] = useState(null);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(() => {
    api.get("/store/orders").then((r) => setOrders(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (o) => {
    const next = openId === o.id ? null : o.id;
    setOpenId(next);
    if (next && o.unread_user > 0) {
      await api.post(`/store/orders/${o.id}/read`).catch(() => {});
      load();
    }
  };

  const send = async (o, fd) => {
    try {
      await api.post(`/store/orders/${o.id}/messages`, fd);
      load();
      return true;
    } catch (e) { toast.error(formatApiError(e)); return false; }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <div className="pt-24 pb-16 max-w-4xl mx-auto px-4 sm:px-6">
        <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Loja de consumo</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold mt-1">Meus <span className="gold-text">pedidos</span></h1>
        <p className="text-sm text-muted-foreground mt-2">Acompanhe seus pedidos da loja e responda às mensagens da equipe.</p>
        <div className="mt-6 space-y-3">
          {!orders ? <p className="text-sm text-muted-foreground">Carregando...</p>
            : orders.length === 0 ? (
              <Card className="p-8 text-center" data-testid="orders-empty">
                <ShoppingBag className="h-8 w-8 mx-auto text-amber-400" />
                <p className="mt-3 text-sm text-muted-foreground">Você ainda não fez pedidos. Visite a loja em Benefícios.</p>
              </Card>
            ) : orders.map((o) => (
              <OrderCard key={o.id} o={o} open={openId === o.id} onToggle={() => toggle(o)} onSend={(t) => send(o, t)} onReload={load} />
            ))}
        </div>
      </div>
    </div>
  );
}
